/**
 * useTutorApplicationIntroState — l'intro du champion d'une étape, port de `ChampionIntroScreen`
 * pour la candidature : les phrases du champion (`championsConstants[<champion><suffixe>]`), une à
 * la fois ; après la dernière, l'entretien de l'étape remplace l'intro (`nextScreen`, avec les
 * paramètres que la page de candidature a posés : lus à l'ouverture, puis repris du store par
 * `pullNextScreenParams`), sinon on revient en arrière (l'intro du champion de l'accueil). Rien n'est envoyé : l'ancien
 * ne marquait l'intro vue que pour un élève.
 */
import { useNavigation } from "@react-navigation/native"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import championsConstants from "../../../../../../data/constants/championsConstants"
import { pullNextScreenParams } from "../../../../../../data/redux/actions/navigation"
import { championNamed } from "../../../../adapters/tutor/Application/tutorApplicationAdapter"

type Navigation = { replace: (route: string, params?: object) => void; goBack: () => void }

export type ApplicationIntroParams = { name?: string; introSuffix?: string; nextScreen?: string | null }

type NavigationState = { navigation?: { nextScreenParams?: object | null } }

/** Les phrases d'une intro, telles que `championsConstants` les tient (suffixe par défaut `_intro`). */
export function introTexts(name: string | undefined, suffix: string | undefined): string[] {
    const entry = (championsConstants as Record<string, { texts?: string[] } | undefined>)[
        `${name ?? ""}${suffix ?? "_intro"}`
    ]
    return entry?.texts ?? []
}

export function useTutorApplicationIntroState(params: ApplicationIntroParams) {
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as Navigation
    const nextScreenParams = useRef(useSelector((state: NavigationState) => state?.navigation?.nextScreenParams))
    useEffect(() => {
        dispatch(pullNextScreenParams() as never)
    }, [dispatch])

    const texts = introTexts(params.name, params.introSuffix)
    const [index, setIndex] = useState(0)

    const onNext = () => {
        if (index < texts.length - 1) {
            setIndex(index + 1)
            return
        }
        if (params.nextScreen) navigation.replace(params.nextScreen, nextScreenParams.current ?? {})
        else navigation.goBack()
    }

    return { champion: championNamed(params.name), texts, index, onNext }
}
